import { Link } from "react-router-dom";
import { Scissors, Sparkles, Heart, Star, Clock, Users, Award, ArrowRight, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_NUMBER = "8898798857";

// Assets
const HERO_IMAGE = "https://images.unsplash.com/photo-1703792686930-9efa64a9c6c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBoYWlyJTIwc2Fsb24lMjBpbnRlcmlvciUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzA2NjAxNjF8MA&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/ccxbep84_Logo.png";
const SALON_IMAGE_1 = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/vqdnwizr_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%2810%29.jpeg";
const SALON_IMAGE_2 = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/2c4is3jk_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%2812%29.jpeg";
const SALON_IMAGE_3 = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/0jeoq4bg_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%283%29.jpeg";

const trustBadges = [
  { icon: Award, title: "20+ Years", subtitle: "Experience" },
  { icon: Users, title: "Family", subtitle: "Friendly" },
  { icon: Star, title: "5 Star", subtitle: "Google Rating" },
  { icon: Clock, title: "Open Daily", subtitle: "9:30AM - 10PM" },
];

const serviceCategories = [
  {
    id: "hair-care",
    title: "Hair Care",
    description: "Expert cuts, colours, and treatments",
    icon: Scissors,
    services: ["Haircuts", "Global Colour", "Balayage", "Highlights", "Keratin"],
    image: SALON_IMAGE_1,
  },
  {
    id: "skincare-spa",
    title: "Skincare & Spa",
    description: "Relaxing treatments for radiant skin",
    icon: Sparkles,
    services: ["Facials", "Hydra-Facials", "Skin Treatments", "Spa"],
    image: "https://images.unsplash.com/photo-1633681121751-e4a0392602b8?w=600&q=80",
  },
  {
    id: "grooming-makeup",
    title: "Grooming & Makeup",
    description: "Complete beauty for every occasion",
    icon: Heart,
    services: ["Bridal Makeup", "Party Makeup", "Manicure", "Pedicure"],
    image: "https://images.unsplash.com/photo-1714381108306-a36169f0d3fb?w=600&q=80",
  },
];

const whyChooseUs = [
  {
    title: "Personalised Consultation",
    description: "Every client receives a thorough hair health analysis and style recommendations tailored to their face shape.",
  },
  {
    title: "Premium Products",
    description: "We use only the finest professional-grade products for lasting results and hair health.",
  },
  {
    title: "Expert Stylists",
    description: "Our team is trained in the latest techniques and trends from around the world.",
  },
  {
    title: "Relaxing Atmosphere",
    description: "Enjoy a premium salon experience in our modern, comfortable space.",
  },
];

export default function HomePage() {

  const handleBookAppointment = () => {
    const message = encodeURIComponent("Hi, I'd like to book an appointment at Bunty Hair Master.");
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-background">
          <img src={HERO_IMAGE} alt="Bunty Hair Master Salon" />
          <div className="hero-gradient"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pt-40 md:pb-32">
          <div className="max-w-2xl">
            <p className="font-['Cormorant_Garamond'] text-[#D4AF37] text-base md:text-lg tracking-[0.2em] uppercase mb-4 animate-fade-in-up">
              Your Family Salon
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-white font-semibold leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Thane's Trusted{" "}
              <span className="gold-text">Hair Experts</span>
            </h1>
            <p className="text-white/70 text-lg md:text-xl mb-8 animate-fade-in-up animation-delay-200">
              20+ years of expertise. Haircuts, colours, bridal makeup & more for the whole family.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Button
                onClick={handleBookAppointment}
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none text-base shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-shadow"
                data-testid="hero-book-btn"
              >
                Book Appointment
              </Button>
              <Button
                onClick={handleBookAppointment}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 rounded-none text-base bg-transparent"
                data-testid="hero-whatsapp-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-[#121212] py-12 md:py-16 border-y border-white/5" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {trustBadges.map((badge, index) => (
              <div key={index} className="trust-badge">
                <div className="trust-badge-icon">
                  <badge.icon className="w-6 h-6" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-white">{badge.title}</h3>
                <p className="text-white/50 text-sm">{badge.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-spacing bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="section-header text-center">
            <p className="section-subtitle">Our Expertise</p>
            <h2 className="section-title">Services We Offer</h2>
          </div>

          <div className="bento-grid">
            {serviceCategories.map((category) => (
              <div 
                key={category.id} 
                className="service-card group relative overflow-hidden"
                data-testid={`service-card-${category.id}`}
              >
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                  <img 
                    src={category.image} 
                    alt={category.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-6">
                    <category.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl text-white mb-3">
                    {category.title}
                  </h3>
                  <p className="text-white/60 mb-6">{category.description}</p>
                  <ul className="space-y-2 mb-6">
                    {category.services.map((service, idx) => (
                      <li key={idx} className="text-white/50 text-sm flex items-center gap-2">
                        <span className="w-1 h-1 bg-[#D4AF37] rounded-full"></span>
                        {service}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/services"
                    className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors text-sm font-medium"
                  >
                    View All Services
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-spacing bg-[#121212]" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="section-subtitle">Why Choose Us</p>
              <h2 className="section-title mb-8">The Bunty Hair Master Difference</h2>
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-[#D4AF37] text-sm font-medium">{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-['Playfair_Display'] text-lg text-white mb-2">{item.title}</h4>
                      <p className="text-white/60 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src={SALON_IMAGE_2}
                  alt="Bunty Sir at work"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] p-6 md:p-8">
                <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-black font-semibold">20+</p>
                <p className="text-black/70 text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Widget */}
      <section className="section-spacing bg-[#0A0A0A]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="section-header text-center">
            <p className="section-subtitle">Testimonials</p>
            <h2 className="section-title">What Our Clients Say</h2>
          </div>
          
          <div className="bg-[#121212] border border-white/5 p-6 md:p-10">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <p className="text-white/60 mb-6">Rated 5 stars on Google</p>
              <a
                href="https://share.google/0z4zwYLOvxkPnyvco"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
                data-testid="google-reviews-link"
              >
                <span>View All Reviews on Google</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <section className="section-spacing bg-[#121212]" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="section-header text-center">
            <p className="section-subtitle">@buntyhairmaster_salon</p>
            <h2 className="section-title">Follow Us on Instagram</h2>
          </div>
          
          <div className="instagram-grid">
            {[HERO_IMAGE, SALON_IMAGE_1, SALON_IMAGE_2, SALON_IMAGE_3, HERO_IMAGE, SALON_IMAGE_1].map((img, index) => (
              <a
                key={index}
                href="https://www.instagram.com/buntyhairmaster_salon/"
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-item"
              >
                <img src={img} alt={`Instagram post ${index + 1}`} />
              </a>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <a
              href="https://www.instagram.com/buntyhairmaster_salon/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold inline-flex items-center gap-2"
              data-testid="instagram-follow-btn"
            >
              Follow @buntyhairmaster_salon
            </a>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-spacing bg-[#0A0A0A]" data-testid="map-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-subtitle">Visit Us</p>
              <h2 className="section-title mb-8">Find Us in Thane West</h2>
              
              <div className="space-y-6">
                <div className="contact-info-item">
                  <div className="contact-icon">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Location</h4>
                    <p className="text-white/60 text-sm">
                      Shop No. 3, Vikas Palms Society,<br />
                      Dr. Ambedkar Road, Uthalsar,<br />
                      Thane West – 400606<br />
                      (Backside Vikas Palms)
                    </p>
                  </div>
                </div>
                
                <div className="contact-info-item">
                  <div className="contact-icon">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Phone</h4>
                    <a 
                      href="tel:+918898798857" 
                      className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
                    >
                      +91 88987 98857
                    </a>
                  </div>
                </div>
                
                <div className="contact-info-item">
                  <div className="contact-icon">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Hours</h4>
                    <p className="text-white/60 text-sm">
                      Open Daily: 9:30 AM - 10:00 PM
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Button
                  onClick={handleBookAppointment}
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium px-8 py-4 rounded-none shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                  data-testid="map-section-book-btn"
                >
                  Book Your Visit
                </Button>
              </div>
            </div>
            
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.5!2d72.9791!3d19.2094!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b8fcdc6fbbcb%3A0x4ae9d7ec18a7e2e9!2sVikas%20Palms%2C%20Thane%20West%2C%20Thane%2C%20Maharashtra%20400606!5e0!3m2!1sen!2sin!4v1234567890"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Bunty Hair Master Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
